import {
  Box,
  Button,
  Card,
  CardHeader,
  Divider,
  Hidden,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTranslation } from "react-i18next";
import {
  Application,
  LinkToShare,
  LinkOrApp,
} from "../apps-components/apps-types";
import { AppItem } from "./LatestApps";
import { LinkItem } from "./LatestLinks";
import NoDataToBeShown from "../reusable/NoDataToBeShown";
import ErrorComponent from "../reusable/ErrorComponent";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import { Link as RouterLink } from "react-router-dom";
import { grey } from "@mui/material/colors";
import { useApps, useLinks } from "../../pages/api/api";

export function Skeleton4LastActivities() {
  return (
    <Stack direction="row" spacing={10}>
      <Stack direction="row" spacing={2}>
        <Skeleton variant="rectangular" width={40} height={40} />
        <Skeleton variant="rectangular" width={210} height={40} />
      </Stack>
      <Skeleton variant="rectangular" width={210} height={40} />
      <Skeleton variant="rectangular" width={210} height={40} />
      <Skeleton variant="rectangular" width={210} height={40} />
    </Stack>
  );
}

function LastActivity(props: { lastActivity: LinkOrApp }) {
  return props.lastActivity.type === "APP" ? (
    <AppItem app={props.lastActivity} />
  ) : (
    <LinkItem link={props.lastActivity} />
  );
}

export default function LastActivities() {
  const { t } = useTranslation();
  const skeletonArray = Array(5).fill("");

  const { apps, isLoadingApps, isErrorApps } = useApps();
  const { links, isLoadingLinks, isErrorLinks } = useLinks();

  const activities: LinkOrApp[] = [
    ...apps.map((u) => ({ ...u, type: "APP" } as Application)),
    ...links.map((u) => ({ ...u, type: "LINK" } as LinkToShare)),
  ];
  const activitiesShort = activities
    .sort((a, b) =>
      new Date(a.updated_at).getTime() < new Date(b.updated_at).getTime()
        ? 1
        : -1
    )
    .slice(0, 5);

  return (
    <Card sx={{ minHeight: 400 }}>
      <CardHeader title={t("Last Activities")} />
      <Hidden mdDown>
        <Typography variant="body2" sx={{ color: "text.secondary", pl: 3 }}>
          {t("*Drag and drop your .apk and .ipa files here...")}
        </Typography>
      </Hidden>
      {isLoadingApps || isLoadingLinks ? (
        <Stack spacing={3} sx={{ p: 3, pr: 0 }}>
          {skeletonArray.map((skeleton, index) => (
            <Skeleton4LastActivities key={index} />
          ))}
        </Stack>
      ) : activities.length > 0 ? (
        <Stack>
          <Stack spacing={3} sx={{ p: 3, pr: 0 }}>
            {activitiesShort.map((activity, index) => (
              <LastActivity key={index} lastActivity={activity} />
            ))}
          </Stack>
          <Divider />
          <Box sx={{ p: 2, textAlign: "right" }}>
            <Button
              to="/applications"
              size="small"
              color="inherit"
              component={RouterLink}
              endIcon={<ArrowForwardIosIcon />}
            >
              {t("View All Applications")}
            </Button>
          </Box>
        </Stack>
      ) : activities.length === 0 && !isErrorApps && !isErrorLinks ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            height: "40vh",
            alignItems: "center",
          }}
        >
          <Stack justifyContent="center" alignItems="center">
            <DriveFolderUploadIcon sx={{ fontSize: 40, color: grey[500] }} />
            <NoDataToBeShown />
            <Typography>{t("Upload a new application to start.")}</Typography>
          </Stack>
        </Box>
      ) : (
        (isErrorApps || isErrorLinks) && <ErrorComponent />
      )}
    </Card>
  );
}
